import { IGetRecordResponse } from '@principle-theorem/practice-migrations/interfaces';
import {
  ExactTransaction,
  ExactTransactionType,
  IExactTransaction,
} from '../source/entities/patient-transactions';

export interface IExactRefundAdjustmentPair {
  /** Exact's invoice-refund adjustment row: the negative adjustment attached to the original invoice. */
  invoiceRefundSourceId: string;
  invoiceRefundReference: string;
  /** Original invoice being refunded. The invoice refund references this invoice number directly. */
  originalInvoiceSourceId?: string;
  originalInvoiceReference?: string;
  originalInvoiceAmount?: number;
  /** Exact's receipt-refund adjustment row: the positive adjustment attached to the original payment. */
  receiptRefundSourceId?: string;
  receiptRefundReference?: string;
  /** Original payment being refunded. The receipt refund references this payment number directly. */
  originalPaymentSourceId?: string;
  originalPaymentReference?: string;
  originalPaymentAmount?: number;
  amount: number;
  /** True when we found multiple plausible receipt refunds and intentionally refused to guess. */
  isAmbiguous: boolean;
}

export interface IExactRefundAdjustmentIndex {
  /** Primary production lookup used by the invoice builder when processing the original invoice. Multiple refund adjustments against the same invoice reference are preserved in source order so every leg can be rendered. */
  pairsByInvoiceReference: ReadonlyMap<string, readonly IExactRefundAdjustmentPair[]>;
  /** Source rows that should not be emitted as standalone transactions. */
  invoiceRefundSourceIds: ReadonlySet<string>;
  /** Receipt-refund rows claimed by a refund pair and attached to the original invoice. */
  pairedReceiptRefundSourceIds: ReadonlySet<string>;
  /** Receipt-refund rows that remain standalone because no safe invoice-refund pair exists. */
  unpairedReceiptRefundSourceIds: ReadonlySet<string>;
}

export function isInvoiceRefundAdjustment(
  transaction: IExactTransaction
): boolean {
  return (
    transaction.type === ExactTransactionType.Adjustment &&
    transaction.amount < 0 &&
    !!transaction.adjustmentTypeLabel &&
    /invoice refund/i.test(transaction.adjustmentTypeLabel)
  );
}

export function isReceiptRefundAdjustment(
  transaction: IExactTransaction
): boolean {
  return (
    transaction.type === ExactTransactionType.Adjustment &&
    transaction.amount > 0 &&
    !!transaction.adjustmentTypeLabel &&
    /receipt refund/i.test(transaction.adjustmentTypeLabel)
  );
}

export function isUntypedRefundLikeAdjustmentCandidate(
  transaction: IExactTransaction
): boolean {
  return (
    transaction.type === ExactTransactionType.Adjustment &&
    !transaction.adjustmentTypeCode &&
    !transaction.adjustmentTypeLabel &&
    !!transaction.reference &&
    !!transaction.date &&
    transaction.amount !== 0
  );
}

function isNonEmptyNumericReference(
  reference: string | null
): reference is string {
  return !!reference && /^\d+$/.test(reference);
}

function buildLookupKey(patientId: string, reference: string): string {
  return `${patientId}:${reference}`;
}

interface IIndexedTransaction {
  index: number;
  data: IExactTransaction;
}

interface IOriginalTransactionLookups {
  originalInvoicesByKey: ReadonlyMap<string, IExactTransaction>;
  originalPaymentsByKey: ReadonlyMap<string, IExactTransaction>;
}

interface IRefundTransactionBuckets {
  invoiceRefunds: IIndexedTransaction[];
  receiptRefunds: IIndexedTransaction[];
}

interface IReceiptRefundSelection {
  pairedReceipt?: IIndexedTransaction;
  isAmbiguous: boolean;
}

interface IRefundAdjustmentIndexState {
  pairsByInvoiceReference: Map<string, IExactRefundAdjustmentPair[]>;
  invoiceRefundSourceIds: Set<string>;
  pairedReceiptRefundSourceIds: Set<string>;
  unpairedReceiptRefundSourceIds: Set<string>;
}

function createRefundAdjustmentIndexState(): IRefundAdjustmentIndexState {
  return {
    pairsByInvoiceReference: new Map<string, IExactRefundAdjustmentPair[]>(),
    invoiceRefundSourceIds: new Set<string>(),
    pairedReceiptRefundSourceIds: new Set<string>(),
    unpairedReceiptRefundSourceIds: new Set<string>(),
  };
}

function indexTransactions(
  sourceTransactions: IGetRecordResponse<ExactTransaction>[]
): IIndexedTransaction[] {
  return sourceTransactions.map((response, index) => ({
    index,
    data: response.data,
  }));
}

function buildOriginalTransactionLookups(
  sourceTransactions: IGetRecordResponse<ExactTransaction>[]
): IOriginalTransactionLookups {
  const originalInvoicesByKey = new Map<string, IExactTransaction>();
  const originalPaymentsByKey = new Map<string, IExactTransaction>();

  for (const response of sourceTransactions) {
    const transaction = response.data;

    if (
      transaction.type === ExactTransactionType.Invoice &&
      isNonEmptyNumericReference(transaction.reference)
    ) {
      const lookupKey = buildLookupKey(
        transaction.patient_id,
        transaction.reference
      );

      if (!originalInvoicesByKey.has(lookupKey)) {
        originalInvoicesByKey.set(lookupKey, transaction);
      }
    }

    if (
      transaction.type === ExactTransactionType.Payment &&
      isNonEmptyNumericReference(transaction.reference) &&
      transaction.amount < 0
    ) {
      const lookupKey = buildLookupKey(
        transaction.patient_id,
        transaction.reference
      );

      if (!originalPaymentsByKey.has(lookupKey)) {
        originalPaymentsByKey.set(lookupKey, transaction);
      }
    }
  }

  return {
    originalInvoicesByKey,
    originalPaymentsByKey,
  };
}

function bucketRefundTransactions(
  indexedTransactions: IIndexedTransaction[]
): IRefundTransactionBuckets {
  const invoiceRefunds: IIndexedTransaction[] = [];
  const receiptRefunds: IIndexedTransaction[] = [];

  for (const indexedTransaction of indexedTransactions) {
    if (isInvoiceRefundAdjustment(indexedTransaction.data)) {
      invoiceRefunds.push(indexedTransaction);
    } else if (isReceiptRefundAdjustment(indexedTransaction.data)) {
      receiptRefunds.push(indexedTransaction);
    }
  }

  return {
    invoiceRefunds,
    receiptRefunds,
  };
}

function findOriginalPayment(
  patientId: string,
  reference: string,
  originalPaymentsByKey: ReadonlyMap<string, IExactTransaction>
): IExactTransaction | undefined {
  return originalPaymentsByKey.get(buildLookupKey(patientId, reference));
}

function isReceiptRefundCandidateForInvoiceRefund(
  invoiceRefund: IExactTransaction,
  receiptRefund: IExactTransaction,
  originalPaymentsByKey: ReadonlyMap<string, IExactTransaction>
): receiptRefund is IExactTransaction & { reference: string } {
  if (receiptRefund.patient_id !== invoiceRefund.patient_id) {
    return false;
  }

  if (receiptRefund.date !== invoiceRefund.date) {
    return false;
  }

  if (
    invoiceRefund.user_code &&
    receiptRefund.user_code &&
    invoiceRefund.user_code !== receiptRefund.user_code
  ) {
    return false;
  }

  if (Math.abs(receiptRefund.amount) !== Math.abs(invoiceRefund.amount)) {
    return false;
  }

  if (!isNonEmptyNumericReference(receiptRefund.reference)) {
    return false;
  }

  const originalPayment = findOriginalPayment(
    receiptRefund.patient_id,
    receiptRefund.reference,
    originalPaymentsByKey
  );
  if (!originalPayment) {
    return false;
  }

  return Math.abs(originalPayment.amount) >= Math.abs(receiptRefund.amount);
}

function findReceiptRefundCandidates(
  invoiceRefund: IExactTransaction,
  receiptRefunds: IIndexedTransaction[],
  originalPaymentsByKey: ReadonlyMap<string, IExactTransaction>
): IIndexedTransaction[] {
  return receiptRefunds.filter((receiptRefund) =>
    isReceiptRefundCandidateForInvoiceRefund(
      invoiceRefund,
      receiptRefund.data,
      originalPaymentsByKey
    )
  );
}

function selectReceiptRefundCandidate(
  invoiceRefund: IIndexedTransaction,
  candidates: IIndexedTransaction[],
  pairedReceiptIndices: ReadonlySet<number>
): IReceiptRefundSelection {
  const availableCandidates = candidates.filter(
    (candidate) => !pairedReceiptIndices.has(candidate.index)
  );

  if (availableCandidates.length === 1) {
    return {
      pairedReceipt: availableCandidates[0],
      isAmbiguous: false,
    };
  }

  if (availableCandidates.length === 0) {
    return {
      isAmbiguous: false,
    };
  }

  // Exact sometimes emits multiple syntactically-valid receipt-refund rows for the
  // same invoice refund. When that happens, source-order adjacency is the safest
  // signal we have for the wizard-generated pair; otherwise we preserve ambiguity.
  const adjacentCandidates = availableCandidates.filter(
    (candidate) =>
      candidate.index === invoiceRefund.index - 1 ||
      candidate.index === invoiceRefund.index + 1
  );

  if (adjacentCandidates.length === 1) {
    return {
      pairedReceipt: adjacentCandidates[0],
      isAmbiguous: false,
    };
  }

  return {
    isAmbiguous: true,
  };
}

function buildRefundAdjustmentPair(
  invoiceRefund: IExactTransaction & { reference: string; treatmentId: string },
  originalInvoice: IExactTransaction,
  pairedReceipt: IIndexedTransaction | undefined,
  originalPaymentsByKey: ReadonlyMap<string, IExactTransaction>,
  isAmbiguous: boolean
): IExactRefundAdjustmentPair {
  const originalPayment =
    pairedReceipt && isNonEmptyNumericReference(pairedReceipt.data.reference)
      ? findOriginalPayment(
          invoiceRefund.patient_id,
          pairedReceipt.data.reference,
          originalPaymentsByKey
        )
      : undefined;

  return {
    invoiceRefundSourceId: invoiceRefund.id,
    invoiceRefundReference: invoiceRefund.reference,
    originalInvoiceSourceId: originalInvoice.id,
    originalInvoiceReference: originalInvoice.reference ?? undefined,
    originalInvoiceAmount: originalInvoice.amount,
    receiptRefundSourceId: pairedReceipt?.data.id,
    receiptRefundReference: pairedReceipt?.data.reference ?? undefined,
    originalPaymentSourceId: originalPayment?.id,
    originalPaymentReference: originalPayment?.reference ?? undefined,
    originalPaymentAmount: originalPayment?.amount,
    amount: Math.abs(invoiceRefund.amount),
    isAmbiguous,
  };
}

function addUnpairedReceiptRefundSourceIds(
  receiptRefunds: IIndexedTransaction[],
  pairedReceiptIndices: ReadonlySet<number>,
  unpairedReceiptRefundSourceIds: Set<string>
): void {
  for (const receiptRefund of receiptRefunds) {
    if (!pairedReceiptIndices.has(receiptRefund.index)) {
      unpairedReceiptRefundSourceIds.add(receiptRefund.data.id);
    }
  }
}

/**
 * Builds a patient-scoped index of Exact refund-adjustment relationships so the
 * invoice builder can turn wizard-generated refund rows into readable invoice
 * line items and preserve the matching payment/refund transactions.
 */
export function buildExactRefundAdjustmentIndex(
  sourceTransactions: IGetRecordResponse<ExactTransaction>[]
): IExactRefundAdjustmentIndex {
  const {
    pairsByInvoiceReference,
    invoiceRefundSourceIds,
    pairedReceiptRefundSourceIds,
    unpairedReceiptRefundSourceIds,
  } = createRefundAdjustmentIndexState();
  const { originalInvoicesByKey, originalPaymentsByKey } =
    buildOriginalTransactionLookups(sourceTransactions);
  const indexedTransactions = indexTransactions(sourceTransactions);
  const { invoiceRefunds, receiptRefunds } =
    bucketRefundTransactions(indexedTransactions);
  const pairedReceiptIndices = new Set<number>();

  for (const indexedInvoiceRefund of invoiceRefunds) {
    const invoiceRefund = indexedInvoiceRefund.data;

    if (
      !isNonEmptyNumericReference(invoiceRefund.reference) ||
      !invoiceRefund.treatmentId
    ) {
      continue;
    }

    const invoiceKey = buildLookupKey(
      invoiceRefund.patient_id,
      invoiceRefund.reference
    );
    const originalInvoice = originalInvoicesByKey.get(invoiceKey);
    if (
      !originalInvoice ||
      originalInvoice.treatmentId !== invoiceRefund.treatmentId
    ) {
      continue;
    }

    const matchedInvoiceRefund = {
      ...invoiceRefund,
      reference: invoiceRefund.reference,
      treatmentId: invoiceRefund.treatmentId,
    };

    invoiceRefundSourceIds.add(matchedInvoiceRefund.id);

    const candidates = findReceiptRefundCandidates(
      matchedInvoiceRefund,
      receiptRefunds,
      originalPaymentsByKey
    );
    const { pairedReceipt, isAmbiguous } = selectReceiptRefundCandidate(
      indexedInvoiceRefund,
      candidates,
      pairedReceiptIndices
    );

    if (pairedReceipt) {
      pairedReceiptIndices.add(pairedReceipt.index);
      pairedReceiptRefundSourceIds.add(pairedReceipt.data.id);
    }

    const pair = buildRefundAdjustmentPair(
      matchedInvoiceRefund,
      originalInvoice,
      pairedReceipt,
      originalPaymentsByKey,
      isAmbiguous
    );
    const existingPairs =
      pairsByInvoiceReference.get(matchedInvoiceRefund.reference) ?? [];
    existingPairs.push(pair);
    pairsByInvoiceReference.set(matchedInvoiceRefund.reference, existingPairs);
  }

  addUnpairedReceiptRefundSourceIds(
    receiptRefunds,
    pairedReceiptIndices,
    unpairedReceiptRefundSourceIds
  );

  return {
    pairsByInvoiceReference,
    invoiceRefundSourceIds,
    pairedReceiptRefundSourceIds,
    unpairedReceiptRefundSourceIds,
  };
}
